import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'
import { logError, logWarn } from '../utils/logger'
import type { Database } from '../types/database'
import { shouldBypassSupabaseRequests } from '../lib/runtimeConfig'

type Order = Database['public']['Tables']['orders']['Row']
type OrderWithProduct = Order & {
  products: Database['public']['Tables']['products']['Row'] | null
}
interface CachedOrdersState {
  orders: OrderWithProduct[]
  hasFetched: boolean
}

const ordersCache = new Map<string, CachedOrdersState>()
const getOrdersCacheStorageKey = (userId: string) =>
  `printboard:orders:${userId}`

const readPersistedOrdersState = (userId?: string): CachedOrdersState => {
  if (!userId || typeof window === 'undefined') {
    return { orders: [], hasFetched: false }
  }

  try {
    const rawValue = window.sessionStorage.getItem(
      getOrdersCacheStorageKey(userId)
    )
    if (!rawValue) return { orders: [], hasFetched: false }

    const parsedValue = JSON.parse(rawValue)

    if (Array.isArray(parsedValue)) {
      return {
        orders: parsedValue as OrderWithProduct[],
        hasFetched: parsedValue.length > 0,
      }
    }

    if (
      parsedValue &&
      typeof parsedValue === 'object' &&
      Array.isArray(parsedValue.orders)
    ) {
      return {
        orders: parsedValue.orders as OrderWithProduct[],
        hasFetched: parsedValue.hasFetched === true,
      }
    }
  } catch {
    return { orders: [], hasFetched: false }
  }

  return { orders: [], hasFetched: false }
}

const writePersistedOrdersState = (
  userId: string,
  state: CachedOrdersState
) => {
  if (typeof window === 'undefined') return

  try {
    window.sessionStorage.setItem(
      getOrdersCacheStorageKey(userId),
      JSON.stringify(state)
    )
  } catch {
    // Ignore storage write failures.
  }
}

interface UseOrdersReturn {
  orders: OrderWithProduct[]
  loading: boolean
  initialLoading: boolean
  refreshing: boolean
  error: string | null
  refetch: () => void
}

interface SyncOrdersOptions {
  forceClear?: boolean
  hasFetched?: boolean
}

export const useOrders = (): UseOrdersReturn => {
  const { user } = useAuth()
  const cachedOrdersState = user?.id
    ? ordersCache.get(user.id) || readPersistedOrdersState(user.id)
    : { orders: [], hasFetched: false }
  const [orders, setOrders] = useState<OrderWithProduct[]>(
    () => cachedOrdersState.orders
  )
  const [loading, setLoading] = useState(() =>
    user?.id ? !cachedOrdersState.hasFetched : true
  )
  const [hasResolvedInitialLoad, setHasResolvedInitialLoad] = useState(() =>
    user?.id ? cachedOrdersState.hasFetched : false
  )
  const [error, setError] = useState<string | null>(null)
  const cacheKeyRef = useRef<string | null>(user?.id || null)

  const syncOrders = useCallback(
    (
      nextOrders:
        | OrderWithProduct[]
        | ((previousOrders: OrderWithProduct[]) => OrderWithProduct[]),
      options: SyncOrdersOptions = {}
    ) => {
      setOrders((previousOrders) => {
        const resolvedOrders =
          typeof nextOrders === 'function'
            ? nextOrders(previousOrders)
            : nextOrders

        if (
          !options.forceClear &&
          previousOrders.length > 0 &&
          resolvedOrders.length === 0
        ) {
          return previousOrders
        }

        if (cacheKeyRef.current) {
          const nextCachedState = {
            orders: resolvedOrders,
            hasFetched: options.hasFetched ?? true,
          }
          ordersCache.set(cacheKeyRef.current, nextCachedState)
          writePersistedOrdersState(cacheKeyRef.current, nextCachedState)
        }

        return resolvedOrders
      })
    },
    []
  )

  const fetchOrders = useCallback(async () => {
    if (!user || shouldBypassSupabaseRequests) {
      cacheKeyRef.current = null
      syncOrders([], { forceClear: true, hasFetched: false })
      setLoading(false)
      setHasResolvedInitialLoad(false)
      setError(null)
      return
    }

    try {
      cacheKeyRef.current = user.id
      const cachedOrdersStateForUser = ordersCache.has(user.id)
        ? ordersCache.get(user.id) || { orders: [], hasFetched: false }
        : readPersistedOrdersState(user.id)
      const cachedOrders = cachedOrdersStateForUser.orders

      if (!cachedOrdersStateForUser.hasFetched) {
        setLoading(true)
      } else {
        syncOrders(cachedOrders, { hasFetched: true })
        setHasResolvedInitialLoad(true)
      }

      setError(null)

      const { data, error: fetchError } = await supabase
        .from('orders')
        .select(
          `
          *,
          products (*)
        `
        )
        .eq('user_id', user.id)
        .eq('payment_status', 'paid')
        .order('created_at', { ascending: false })

      if (fetchError) {
        throw fetchError
      }

      const fetchedOrders = data as OrderWithProduct[]

      if (cachedOrders.length > 0 && fetchedOrders.length === 0) {
        logWarn(
          'useOrders',
          'Ignoring empty orders refresh while cache exists',
          {
            userId: user.id,
            cachedCount: cachedOrders.length,
          }
        )
        return
      }

      syncOrders(fetchedOrders)
    } catch (err) {
      if ((ordersCache.get(user.id)?.orders.length || 0) === 0) {
        setError(err instanceof Error ? err.message : 'Failed to fetch orders')
        syncOrders([], { forceClear: true, hasFetched: false })
      }
    } finally {
      setLoading(false)
      setHasResolvedInitialLoad(true)
    }
  }, [syncOrders, user])

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  const refetch = useCallback(() => {
    fetchOrders()
  }, [fetchOrders])

  return {
    orders,
    loading,
    initialLoading: !hasResolvedInitialLoad && loading,
    refreshing: hasResolvedInitialLoad && loading,
    error,
    refetch,
  }
}

// Hook for a single order
export const useOrder = (orderId: string | null) => {
  const [order, setOrder] = useState<OrderWithProduct | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchOrder = useCallback(async () => {
    if (!orderId || shouldBypassSupabaseRequests) {
      setOrder(null)
      setLoading(false)
      setError(null)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('orders')
        .select(
          `
          *,
          products (*)
        `
        )
        .eq('id', orderId)
        .single()

      if (fetchError) {
        throw fetchError
      }

      setOrder(data as OrderWithProduct)
    } catch (err) {
      logError('useOrders', 'Error fetching order:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch order')
    } finally {
      setLoading(false)
    }
  }, [orderId])

  useEffect(() => {
    fetchOrder()
  }, [fetchOrder])

  return {
    order,
    loading,
    error,
    refetch: fetchOrder,
  }
}

// Utility functions for orders
export const formatOrderPrice = (cents: number): string => {
  return `$${(cents / 100).toFixed(2)}`
}

export const formatOrderDate = (dateString: string): string => {
  const value = new Date(dateString)
  if (Number.isNaN(value.getTime())) return ''

  return value.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  })
}

export const getOrderStatusColor = (status: Order['order_status']): string => {
  switch (status) {
    case 'pending':
      return 'bg-accent-100/20 text-neutral-ink-700 border-accent-100/40'
    case 'processing':
      return 'bg-secondary-300/20 text-neutral-ink-700 border-secondary-300/40'
    case 'shipped':
      return 'bg-primary-300/20 text-neutral-ink-700 border-primary-300/40'
    case 'delivered':
      return 'bg-state-success/20 text-neutral-ink-700 border-state-success/40'
    case 'cancelled':
      return 'bg-state-error/20 text-neutral-ink-700 border-state-error/40'
    default:
      return 'bg-neutral-stone-300/30 text-neutral-ink-700 border-neutral-stone-300'
  }
}

export const getFulfillmentStatusColor = (
  status: Order['fulfillment_status']
): string => {
  switch (status) {
    case 'unfulfilled':
      return 'bg-neutral-stone-300/30 text-neutral-ink-700 border-neutral-stone-300'
    case 'waiting_for_print_asset':
      return 'bg-secondary-300/20 text-neutral-ink-700 border-secondary-300/40'
    case 'processing':
      return 'bg-secondary-300/20 text-neutral-ink-700 border-secondary-300/40'
    case 'printed':
      return 'bg-primary-400/20 text-neutral-ink-700 border-primary-400/40'
    case 'shipped':
      return 'bg-accent-300/20 text-neutral-ink-700 border-accent-300/40'
    case 'delivered':
      return 'bg-state-success/20 text-neutral-ink-700 border-state-success/40'
    case 'cancelled':
      return 'bg-state-error/20 text-neutral-ink-700 border-state-error/40'
    default:
      return 'bg-neutral-stone-300/30 text-neutral-ink-700 border-neutral-stone-300'
  }
}

export const getTrackingUrl = (
  trackingNumber: string,
  carrier?: string
): string => {
  // Default to UPS if no carrier specified
  const normalizedCarrier = carrier?.toLowerCase() || 'ups'

  switch (normalizedCarrier) {
    case 'ups':
      return `https://www.ups.com/track?loc=en_US&tracknum=${trackingNumber}`
    case 'fedex':
      return `https://www.fedex.com/apps/fedextrack/?action=track&trackingnumber=${trackingNumber}`
    case 'usps':
      return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`
    case 'dhl':
      return `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${trackingNumber}`
    default:
      return `https://www.ups.com/track?loc=en_US&tracknum=${trackingNumber}`
  }
}
